import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiMail, FiArrowLeft } from 'react-icons/fi';
import Navbar from '../components/Navbar';
import { authAPI } from '../services/api';
import logger from '../utils/logger';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email) {
      setError('Please enter your email address');
      setLoading(false);
      return;
    }

    try {
      logger.info('Requesting password reset', { email });
      await authAPI.forgotPassword(email);
      setSuccess(true);
      logger.info('Password reset email sent', { email });
    } catch (err) {
      setError(err.message || 'Failed to send reset email');
      logger.error('Forgot password error', { email, error: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-950 min-h-screen">
      <Navbar hideAuthButtons={true} />
      
      <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 min-h-[calc(100vh-64px)]">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-6xl font-bold text-white mb-4 tracking-wider uppercase">Forgot</h1>
            <h2 className="text-4xl font-bold text-red-500 mb-6 tracking-widest uppercase">Password</h2>
            <div className="w-16 h-1.5 bg-red-500 mx-auto mb-6"></div>
            <p className="text-gray-400 text-sm font-medium">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>

          {/* Form Container */}
          <div className="bg-gray-900 border-2 border-gray-600 rounded-lg p-8 mb-8 relative overflow-hidden">
            {/* Subtle Gradient Background */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 via-red-600 to-red-500"></div>
            
            {success ? (
              <div className="text-center py-6">
                <div className="w-20 h-20 bg-green-500/20 border-2 border-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FiMail className="w-10 h-10 text-green-500" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Check Your Email</h3>
                <p className="text-gray-400 text-sm mb-8">
                  We've sent a password reset link to <span className="text-white font-bold">{email}</span>.
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center text-red-500 hover:text-red-400 font-bold transition text-sm uppercase tracking-wider"
                >
                  <FiArrowLeft className="mr-2" /> Back to Login
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
                    Email Address
                  </label>
                  <div className="relative">
                    <FiMail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 text-white placeholder-gray-600 rounded focus:outline-none focus:border-red-500 transition text-sm"
                    />
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3">
                    <p className="text-red-400 text-xs text-center">{error}</p>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-red-500 hover:bg-red-600 disabled:bg-red-700 text-white font-bold py-3 px-4 rounded transition duration-200 text-sm uppercase tracking-wider shadow-lg shadow-red-500/20"
                >
                  {loading ? 'SENDING LINK...' : 'SEND RESET LINK'}
                </button>

                <div className="text-center pt-2">
                  <Link
                    to="/login"
                    className="inline-flex items-center text-gray-400 hover:text-white text-xs font-bold transition uppercase tracking-widest"
                  >
                    <FiArrowLeft className="mr-2" /> Back to Login
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
